import React, { createContext, useContext, useState, useEffect } from 'react';
import { useParams, useSearchParams } from "react-router-dom"
import { useAppCtx, appStore } from "lib/state"
import { queryTools } from "lib/utils"
import _ from "lodash"
import dayjs from "dayjs"
import { parseContent } from "lib/render/parseContent"

const TemplatePreviewContext = createContext({});
export const useTemplatePreviewCtx = () => useContext(TemplatePreviewContext);


export default function TemplatePreviewProvider({
  mode="render",
  datasource="test",
  rp,
  subdomain,
  template,
  bypass,
  renderconfig,
  children
}:{
  mode:string
  datasource?:string
  rp?:any
  subdomain:string
  template?:any
  bypass?:string
  renderconfig?:{ width:string, height:string, scale:number }
  children:React.ReactNode
}) {

  const mocks = {
    rp: '257',
    subdomain: 'woopla',
    mode: 'render'
  }

  // const { tmplid } = useParams()
  // const [ searchParams, setSearchParams ] = useSearchParams();

  const { appState } = useAppCtx()
  const { store, AppStore, refs }:any = appStore()  

  const [ checklist, setChecklist ] = useState<any>({ errors: 0, complete: 0 })

  const [ elementsConfig, setElementsConfig ] = useState<any>({ src: "", data: null})

  const { data:_ApiData } = queryTools.resource.parseQueryForApi("apidata.findAll", {})
  
  const { data:_Client, error: _ClientError, isFetching: _ClientLoading } 
    = queryTools.resource.parseQueryForApi("client.findOne", { subdomain }, { enabled: !!subdomain })

  const { data:_Representment, error: _RepresentmentError, isFetching: _RepresentmentLoading } 
    = queryTools.resource.parseQueryForApi( "rp.findOne",  { rpid: rp, subdomain }, { enabled: !!rp })

  const { data:_Merchant, error: _MerchantError, isFetching: _MerchantLoading  } 
    = queryTools.resource.parseQueryForApi( "merchant.findOne", { merchid: rp, subdomain }, { enabled: !!rp } )
  

  // const disputeParams = { representment_id: _Representment?._id, subdomain: subdomain, client_id: _Client?._id, midmetrics_id: template.Id }
  // const { data:_Dispute } = queryTools.resource.parseQueryForApi("dispute.findOne", disputeParams)


  const getRepresentmentData = ({ data, path }:{ data:any; path:string }) => {
    if( path.startsWith("rp.") ){ path = path.split("rp.")[1] }
    return _.get(data, path)
  }

  const getMerchantData = ({ data, path }:{ data:any; path:string }) => {
    if( path.startsWith("merchant.") ){ path = path.split("merchant.")[1] }
    if( path.startsWith("merch.") ){ path = path.split("merch.")[1] }
    const result = _.get(data, path)
    return result
  }

  const getFromRp = (path:string) => getRepresentmentData({ data: _Representment, path })
  const getFromMerch = (path:string) => getMerchantData({ data: _Merchant, path })
  const getApiFeedData = (path:string) => (path.startsWith("rp.")) ? getFromRp(path) : getFromMerch(path)

  /** This is basically a wrapper for the content parser at lib/render/parseContent */
  const parseApiData = (str:string) => {
    return parseContent({
      content: str,
      clientDataObj: { rp: _Representment, merchant: _Merchant },
      apiData: _ApiData
    })
  }

  const getElementConfig = (element:string) => {
    let config = null 
    const templateId = template._id

    // a customized dispute template wins over everything
    if( elementsConfig.src === "dispute" ){
      config = elementsConfig.data.find( (elem:any) => elem.refstr === element )?.config
    } else {
      //  check first for client-specific config for this template
      if( bypass !== 'client-template' ){
        const clientConfigForTemplate = _Client?.configs?.templates?.byId.find( (tmpl:any) => tmpl._id === templateId )?.[element]
        if( clientConfigForTemplate ){
          config = clientConfigForTemplate
        }
      }

      // if( !config && bypass !== 'client' ){
      //   // check for general client config for all templates
      //   // console.log(element)
      //   const clientConfig = _Client?.configs?.templates?.all?.[element]?.rendering
      //   if( clientConfig ){
      //     config = clientConfig
      //   }
      // }

      if( !config ){
        // use the config associated w/ the template
        config = template.Elements.find( (elem:any) => elem.refstr === element )?.config
      }
    }
    return config
  }

  const formatter = {
    currency: (data:any, type:any) => {
      if( !data ) return ""
      let currencySymbol = (type === "USD") ? "$" : (type === "EUR") ? "€" : ""
      return `${currencySymbol}${parseFloat(data).toFixed(2)}`
    },

    date: (data:any, configStr:any) => {
      if( !data ) return ""
      let format = configStr || "MM/DD/YYYY"
      return dayjs(data).format(format)
    }
  }

  // const getConfigSource = () => {
  //   let config
  //   if( _Dispute && _Dispute.elements && _Dispute.active === true ){
  //     config = _Dispute.elements
  //     setElementsConfig({ src: "dispute", data: _Dispute.elements })
  //   } else {
  //     config = template.Elements
  //     setElementsConfig({ src: "template", data: template.Elements })
  //   }
  // }

  const getConfigSource = () => {
    let config = template.Elements
    setElementsConfig({ src: "template", data: template.Elements })
  }

  useEffect(() => {
    if( _Client ){ setChecklist((prev:any) => ({...prev, complete: prev.complete + 1 }) ) }
    if( _ClientError ){ setChecklist((prev:any) => ({...prev, errors: prev.errors + 1 }) ) }
  },[_Client, _ClientError])

  useEffect(() => {
    if( _Representment ){ setChecklist((prev:any) => ({...prev, complete: prev.complete + 1 }) ) }
    if( _RepresentmentError ){ setChecklist((prev:any) => ({...prev, errors: prev.errors + 1 }) ) }
  },[_Representment, _RepresentmentError])

  useEffect(() => {
    if( _Merchant ){ setChecklist((prev:any) => ({...prev, complete: prev.complete + 1 }) ) }
    if( _MerchantError ){ setChecklist((prev:any) => ({...prev, errors: prev.errors + 1 }) ) }
  },[_Merchant, _MerchantError])

  useEffect(() => {
    console.log(checklist)
  },[checklist])

  // useEffect(() => {
  //   renderconfig
  // },[renderconfig])

  useEffect(() => {
    getConfigSource()
  },[template, _Representment])

  useEffect(() => {
    console.log(checklist)
  },[checklist])


  if( checklist.errors === 3 ){
    return (
      <div className="w-full :*text-white">
        <p>Unable to render template:</p>
        <p>Representment: { _Representment ? "available" : "unavailable" }</p>
        <p>Merchant: { _Merchant ? "available" : "unavailable" }</p>
        <p>Client: { _Client ? "available" : "unavailable" }</p>
        <p>Template: { template ? "available" : "unavailable" }</p>
        <p>Elements Config: { elementsConfig.data ? "available" : "unavailable" }</p>
        <p>Render Config: { renderconfig ? "available" : "unavailable" }</p>

      </div>
    )
  }

  if( checklist.complete === 3 ){
    return (
      <div id="mm-pdf-container">
        <TemplatePreviewContext.Provider 
          value={{
            rp: _Representment,
            merch: _Merchant,
            clientApiData: { rp: _Representment, merchant: _Merchant },
            template: template,
            client: template.AssignedTo || _Client,
            useTailwind: false,
            subdomain,
            mode,
            getFromRp,
            getFromMerch,
            getApiFeedData,
            getElementConfig,
            formatter,
            renderconfig,
            parseApiData,
            mocks
          }}
        >
          {children}
        </TemplatePreviewContext.Provider>
      </div>
    );
  }

  return <></>
}